import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share2, ChevronLeft } from 'lucide-react';
import useParams from '../hooks/useParams';
import { apiFetch } from '../utils/api';
import SessionShareModal from '../components/SessionShareModal';
import { useTranslation } from 'react-i18next';
import './SessionDetailPage.css';

const parseCSV = (str) => String(str || '').split(',').map(v => v.trim()).filter(Boolean);

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  useEffect(() => { fetchSession(); }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await apiFetch(`/api/sessions/${sessionId}`);
      if (response.ok) setSession(await response.json());
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const s = new Date(dateStr + 'T12:00:00').toLocaleDateString(dateLocale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // Compute session-level totals
  const getSessionStats = (exercises) => {
    let totalSets = 0;
    let totalVolume = 0;
    for (const ex of exercises) {
      const weights = parseCSV(ex.weight).map(Number);
      const repsList = parseCSV(ex.reps).map(Number);
      totalSets += Math.max(weights.length, repsList.length);
      totalVolume += weights.reduce((s, w, i) => {
        const r = repsList[i] ?? 0;
        return isNaN(w) || isNaN(r) ? s : s + w * r;
      }, 0);
    }
    return { totalSets, totalVolume };
  };

  const formatDuration = (secs) => {
    if (!Number.isFinite(secs) || secs <= 0) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    const s = secs % 60;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  if (!session) {
    return (
      <div className="page-container animate-fade-in">
        <button className="workout-page-back" onClick={() => navigate(-1)}>
          <ChevronLeft size={14} /> {t('common.back')}
        </button>
        <div className="empty-state">
          <p className="empty-title">{t('session_detail.not_found')}</p>
        </div>
      </div>
    );
  }

  const hasExercises = session.exercises && session.exercises.length > 0;
  const { totalSets, totalVolume } = hasExercises
    ? getSessionStats(session.exercises)
    : { totalSets: 0, totalVolume: 0 };
  const durationLabel = formatDuration(session.duration_seconds);

  return (
    <>
      <div className="page-container animate-fade-in">

        {/* ── Header row ───────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <button className="workout-page-back" onClick={() => navigate(-1)}>
            <ChevronLeft size={14} /> {t('common.back')}
          </button>
          {hasExercises && (
            <button
              className="btn btn-sm btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => setShowShare(true)}
            >
              <Share2 size={14} />
              {t('session_detail.share_btn')}
            </button>
          )}
        </div>

        {!hasExercises ? (
          <div className="empty-state">
            <p className="empty-title">{t('session_detail.no_exercises_title')}</p>
            <p className="empty-desc">{t('session_detail.no_exercises_desc')}</p>
          </div>
        ) : (
          <>
            {/* ── Session info card ─────────────────────────────── */}
            <div className="sd-info-card">
              <h1 className="sd-day-name">{session.day_name}</h1>
              <p className="sd-workout-name">{session.workout_name}</p>
              <p className="sd-date">{formatDate(session.date)}</p>

              <div className="sd-summary-stats">
                <div className="sd-stat">
                  <span className="sd-stat-value">{session.exercises.length}</span>
                  <span className="sd-stat-label">
                    {t('session_detail.col_exercise', { defaultValue: 'ejercicios' })}
                  </span>
                </div>
                <div className="sd-stat-divider" />
                <div className="sd-stat">
                  <span className="sd-stat-value">{totalSets}</span>
                  <span className="sd-stat-label">series</span>
                </div>
                {totalVolume > 0 && (
                  <>
                    <div className="sd-stat-divider" />
                    <div className="sd-stat">
                      <span className="sd-stat-value">
                        {Math.round(totalVolume).toLocaleString()}
                      </span>
                      <span className="sd-stat-label">kg vol.</span>
                    </div>
                  </>
                )}
                {durationLabel && (
                  <>
                    <div className="sd-stat-divider" />
                    <div className="sd-stat">
                      <span className="sd-stat-value">{durationLabel}</span>
                      <span className="sd-stat-label">{t('session_detail.duration_label')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Exercise cards ────────────────────────────────── */}
            {session.exercises.map((ex, i) => {
              const weights  = parseCSV(ex.weight);
              const reps     = parseCSV(ex.reps);
              const maxSets  = Math.max(weights.length, reps.length);

              const numWeights = weights.map(Number).filter(n => !isNaN(n) && n > 0);
              const numReps    = reps.map(Number).filter(n => !isNaN(n) && n > 0);
              const maxWeight  = numWeights.length > 0 ? Math.max(...numWeights) : 0;
              const exVolume   = numWeights.reduce((sum, w, idx) => sum + w * (numReps[idx] ?? 0), 0);

              return (
                <div key={i} className="sd-exercise-card">

                  {/* Header: name + series badge */}
                  <div className="sd-exercise-header">
                    <p className="sd-exercise-name">{ex.exercise_name}</p>
                    {maxSets > 0 && (
                      <span className="sd-sets-badge">{maxSets} series</span>
                    )}
                  </div>

                  {maxSets > 0 && (
                    <>
                      {/* Per-set rows */}
                      <div className="sd-sets-list">
                        {Array.from({ length: maxSets }, (_, sIdx) => {
                          const w = weights[sIdx];
                          const r = reps[sIdx];
                          return (
                            <div key={sIdx} className="sd-set-row">
                              <span className="sd-set-num">S{sIdx + 1}</span>

                              <div className="sd-set-weight-cell">
                                {w ? (
                                  <>
                                    <span className="sd-set-value-weight">{w}</span>
                                    <small className="sd-set-unit">kg</small>
                                  </>
                                ) : (
                                  <span className="sd-set-dash">—</span>
                                )}
                              </div>

                              <span className="sd-set-sep">×</span>

                              <div className="sd-set-reps-cell">
                                {r ? (
                                  <>
                                    <span className="sd-set-value-reps">{r}</span>
                                    <small className="sd-set-unit">reps</small>
                                  </>
                                ) : (
                                  <span className="sd-set-dash">—</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Stats footer */}
                      {(maxWeight > 0 || exVolume > 0) && (
                        <div className="sd-exercise-footer">
                          {maxWeight > 0 && (
                            <span className="sd-stat-chip sd-stat-best">
                              🏋️ {maxWeight}kg max
                            </span>
                          )}
                          {exVolume > 0 && (
                            <span className="sd-stat-chip sd-stat-vol">
                              ⚡ {Math.round(exVolume).toLocaleString()}kg vol.
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Notes */}
                  {ex.notes && (
                    <p className="sd-exercise-notes">"{ex.notes}"</p>
                  )}
                </div>
              );
            })}

            {/* ── Footer ───────────────────────────────────────── */}
            <div className="sd-footer">
              <span className="sd-footer-badge">
                {t('session_detail.exercises_logged', { count: session.exercises.length })}
              </span>
            </div>
          </>
        )}
      </div>

      {showShare && (
        <SessionShareModal
          session={session}
          onClose={() => setShowShare(false)}
        />
      )}
    </>
  );
};

export default SessionDetailPage;
